import React, { useState } from "react";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";

const GroupTourTravellers = ({ tour, user, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState(null);

    const travellers = tour.travellers || [];

    const initialForm = {
        firstName: "",
        lastName: "",
        passportNumber: "",
        panNumber: "",
        documentsPreview: [], // For the UI list
        rawFiles: []         // For the actual binary upload
    };

    const [formData, setFormData] = useState(initialForm);

    const toggleExpand = (index) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // 1. Create previews for UI
        const newPreviews = files.map(file => ({
            fileName: file.name,
            sizeKb: Math.round(file.size / 1024)
        }));

        setFormData(prev => ({
            ...prev,
            documentsPreview: [...prev.documentsPreview, ...newPreviews],
            rawFiles: [...prev.rawFiles, ...files] // Store actual File objects
        }));
        
        e.target.value = null;
    };

    const removeFileFromForm = (index) => {
        setFormData(prev => ({
            ...prev,
            documentsPreview: prev.documentsPreview.filter((_, i) => i !== index),
            rawFiles: prev.rawFiles.filter((_, i) => i !== index)
        }));
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // IMPORTANT: Create FormData for [FromForm] backend
            const data = new FormData();
            data.append("FirstName", formData.firstName);
            data.append("LastName", formData.lastName);
            data.append("PanNumber", formData.panNumber);
            data.append("PassportNumber", formData.passportNumber);

            // Append each binary file to "DocumentFiles" (Must match C# property name)
            formData.rawFiles.forEach((file) => {
                data.append("DocumentFiles", file);
            });

            const url = `${config.ADDTRAVELLER}?groupTourId=${tour.groupTourId}`;
            
            // Ensure your apiService sends this with 'multipart/form-data' header if needed
            // Usually, axios/fetch does this automatically when passing FormData
            await apiService.create(url, data);

            setFormData(initialForm);
            setShowAddForm(false);
            if (onRefresh) onRefresh(); 
        } catch (err) {
            console.error("Add Traveller Error:", err);
        } finally { setLoading(false); }
    };

    return (
        <div className="d-flex flex-column h-100 bg-white">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light shadow-sm">
                <div>
                    <h6 className="fw-bold mb-0">Group Members</h6>
                    <small className="text-muted">{travellers.length} registered</small>
                </div>
                <button className={`btn btn-sm ${showAddForm ? 'btn-danger' : 'btn-primary'}`} onClick={() => setShowAddForm(!showAddForm)}>
                    {showAddForm ? "Cancel" : "+ Add Member"}
                </button>
            </div>

            <div className="flex-grow-1 overflow-auto p-3 bg-light-subtle">
                {showAddForm && (
                    <form onSubmit={handleAdd} className="bg-white p-3 border rounded shadow-sm mb-4 animate__animated animate__fadeIn">
                        <div className="row mb-3 align-items-center">
                            <div className="col">
                                <h6 className="fw-bold text-primary mb-0">Register New Traveller</h6>
                            </div>
                            <div className="col-auto">
                                <button type="submit" className="btn btn-success fw-bold shadow-sm" disabled={loading}>
                                    {loading ? "Saving..." : "Save Member"}
                                </button>
                            </div>
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-3">
                                <label className="small fw-bold">FIRST NAME</label>
                                <input type="text" className="form-control form-control-sm" required value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} />
                            </div>
                            <div className="col-3">
                                <label className="small fw-bold">LAST NAME</label>
                                <input type="text" className="form-control form-control-sm" required value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} />
                            </div>
                            <div className="col-3">
                                <label className="small fw-bold">PAN NUMBER</label>
                                <input type="text" className="form-control form-control-sm" value={formData.panNumber} onChange={(e) => setFormData({...formData, panNumber: e.target.value})} />
                            </div>
                            <div className="col-3">
                                <label className="small fw-bold">PASSPORT NO.</label>
                                <input type="text" className="form-control form-control-sm" value={formData.passportNumber} onChange={(e) => setFormData({...formData, passportNumber: e.target.value})} />
                            </div>
                        </div>
                        <div className="row g-2 mb-2">
                            <div className="col-4 mb-3">
                                <label className="small fw-bold text-primary">UPLOAD DOCUMENTS (Multiple)</label>
                                <input type="file" className="form-control form-control-sm" onChange={handleFileChange} multiple />
                                
                                <div className="mt-2">
                                    {formData.documentsPreview.map((doc, idx) => (
                                        <div key={idx} className="bg-light p-1 px-2 rounded border small mb-1 d-flex justify-content-between align-items-center">
                                            <span className="text-truncate" style={{maxWidth: '85%'}}><i className="ri-file-line me-1"></i>{doc.fileName}</span>
                                            <i className="ri-delete-bin-line text-danger cursor-pointer" onClick={() => removeFileFromForm(idx)}></i>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </form>
                )}

                {/* Traveller List Section remains the same as your previous version */}
                {travellers.length > 0 ? (
                    <div className="vstack gap-2">
                        {travellers.map((t, i) => (
                            <div key={i} className="card border-0 shadow-sm mb-0 overflow-hidden">
                                <div className="card-header bg-white p-2 d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => toggleExpand(i)}>
                                    <div className="avatar-xs bg-soft-info text-info rounded-circle d-flex align-items-center justify-content-center fw-bold me-2">
                                        {t.firstName?.charAt(0)}{t.lastName?.charAt(0)}
                                    </div>
                                    <div className="flex-grow-1">
                                        <h6 className="mb-0 fs-13 fw-bold text-dark">{t.firstName} {t.lastName}</h6>
                                        <div className="text-muted small" style={{fontSize: '10px'}}>PAN: {t.panNumber || 'N/A'}</div>
                                    </div>
                                    <i className={`ri-arrow-${expandedIndex === i ? 'up' : 'down'}-s-line fs-18 text-muted`}></i>
                                </div>

                                {expandedIndex === i && (
                                    <div className="card-body p-3 bg-light border-top animate__animated animate__fadeIn">
                                        <div className="row g-2 mb-3">
                                            <div className="col-6">
                                                <small className="text-muted d-block fw-bold" style={{fontSize: '10px'}}>PASSPORT NUMBER</small>
                                                <span className="fs-12 text-dark">{t.passportNumber || 'N/A'}</span>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-muted d-block fw-bold" style={{fontSize: '10px'}}>PAN NUMBER</small>
                                                <span className="fs-12 text-dark">{t.panNumber || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <h6 className="fs-11 fw-bold text-primary mb-2 border-bottom pb-1">UPLOADED DOCUMENTS</h6>
                                        {t.media?.documents?.length > 0 ? (
                                            <div className="vstack gap-1">
                                                {t.media.documents.map((doc, dIdx) => (
                                                    <div key={dIdx} className="d-flex align-items-center justify-content-between bg-white p-2 rounded border small">
                                                        <div className="d-flex align-items-center text-truncate">
                                                            <i className="ri-file-line text-info me-2"></i>
                                                            <span className="text-truncate" style={{maxWidth: '180px'}} title={doc.fileName}>{doc.fileName}</span>
                                                        </div>
                                                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-soft-primary p-1 px-2 border-0">
                                                            <i className="ri-eye-line me-1"></i> View
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-muted small italic mb-0">No documents found.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (!showAddForm && (
                    <div className="text-center py-5 text-muted">
                        <i className="ri-group-line display-4 opacity-25"></i>
                        <p className="mt-2">No travellers added yet.</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default GroupTourTravellers;